import { useState, useEffect, useRef, forwardRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Axios, { markPageUnloading } from "@/shared/api/axios";
import { useSelector, useDispatch } from "react-redux";
import {
  setIdolId,
  setIdolUuid,
  setNickName,
  setFullName,
  setAvatarUrl,
  setCoverUrl,
  setIsOfficial,
} from "@/shared/store/slices/userSlice";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import i18n from "@/localization/i18n";
import NotificationDropdown from "@/components/layout/NotificationDropdown";
import IconButton from "@/components/ui/IconButton";
import IdolCoverCard from "@/components/ui/IdolCoverCard";
import StatusModal from "@/components/ui/StatusModal";
import SuspendedIdolItem from "@/components/ui/SuspendedIdolItem";
import { persistor } from "@/shared/store/store";
import { useSignout } from "@/hooks/useSignout";
import { useDebouncedValue, SEARCH_DEBOUNCE_MS } from "@/hooks/useDebouncedValue";
import { useCompositionSettledValue } from "@/hooks/useCompositionSettledValue";
import { isImeComposingKey } from "@/shared/utils/imeKeyboard";

import placeholder_portrait_40x40 from "@/assets/imgs/placeholders/portrait_40x40.png";
import placeholder_portrait_35x35 from "@/assets/imgs/placeholders/portrait_35x35.png";
import SafeImg from "@/components/shared/SafeImg";
import IdolInline from "@/components/ui/IdolInline";
import logo_small from "@/assets/imgs/logo/logo_166x88.png";

import {
  Button,
  Container,
  Dropdown,
  Form,
  Nav,
  Navbar,
} from "react-bootstrap";

// 這兩個 navbar toggle 刻意保留 dropdown-toggle class（caret 已由 custom.scss 的
// `.navbar-wrap .dropdown-toggle::after { display: none }` 關掉），與其他 Dropdown toggle
// 移除它的理由不同。具名解構的目的只是不讓 react-bootstrap 注入的 props 靜默流失 ——
// 需要的是 aria-expanded，id 則是呼叫端指定、原樣轉發。
// 註：若日後 Dropdown.Menu 加上 role="menu"，記得一併轉發 aria-haspopup
const IconToggle = forwardRef(({ onClick, id, tooltip, 'aria-expanded': expanded }, ref) => (
  <IconButton ref={ref} aria-expanded={expanded} id={id} size="lg" className="bg-white nav-link dropdown-toggle border-0 p-0" tooltip={tooltip} placement="bottom" onClick={onClick}>
    <FontAwesomeIcon icon={["fas", "language"]} />
  </IconButton>
));
IconToggle.displayName = 'IconToggle';

// 頭像本身是 alt="" 的裝飾性圖片，按鈕沒有可讀名稱，補 aria-label 才不會只念出「按鈕，已收合」
const AvatarToggle = forwardRef(({ onClick, id, src, placeholder, 'aria-expanded': expanded }, ref) => {
  const { t } = useTranslation();
  return (
  <button
    ref={ref}
    id={id}
    type="button"
    className="nav-link dropdown-toggle border-0 bg-transparent p-0"
    aria-label={t('account')}
    aria-expanded={expanded}
    onClick={onClick}
  >
    <div className="img-sm">
      <SafeImg className="rounded-pill" src={src || placeholder} alt="" />
    </div>
  </button>
  );
});
AvatarToggle.displayName = 'AvatarToggle';

function MainNav({ guestMode = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // 搜尋
  const [searchResultVisible, setSearchResultVisible] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  // 搜尋結果連同它所屬的關鍵字一起存：{ keyword, list }。關鍵字改了、新結果還沒回來前不顯示舊關鍵字的結果
  const [searchResult, setSearchResult] = useState(null);
  const searchInputRef = useRef(null);
  // 停頓後才送出搜尋，避免每打一個字就送一支請求；輸入法組字期間不送出
  const { settledValue: settledSearchKeyword, compositionProps: searchCompositionProps } = useCompositionSettledValue(searchKeyword);
  const debouncedSearchKeyword = useDebouncedValue(settledSearchKeyword, SEARCH_DEBOUNCE_MS);
  // 防亂序：只套用最後一次搜尋的結果，避免較慢的舊關鍵字回應蓋掉新關鍵字的結果
  const searchSeqRef = useRef(0);
  // 搜尋失敗的關鍵字：這個關鍵字不再顯示「載入中」（錯誤已由 StatusModal／ServerErrorPage 顯示）
  const [searchFailedKeyword, setSearchFailedKeyword] = useState(null);

  // 訊息
  const [unreadCount, setUnreadCount] = useState(0);

  // 切換偶像
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');
  const idolSwitchingRef = useRef(false);

  // 主偶像資料
  const token = useSelector((state) => state.user.token);
  const id = useSelector((state) => state.user.idolId);

  // guestMode 時強制以未登入狀態渲染（不影響實際 token）
  const effectiveToken = guestMode ? null : token;

  const idolUuid = useSelector((state) => state.user.idolUuid);
  const avatarUrl = useSelector((state) => state.user.avatarUrl);
  const nickName = useSelector((state) => state.user.nickName);
  const fullName = useSelector((state) => state.user.fullName);
  const isOfficial = useSelector((state) => state.user.isOfficial);
  const coverUrl = useSelector((state) => state.user.coverUrl);
  const idolList = useSelector((state) => state.idol.list);

  const syncIdolData = () => {
    if (!idolList || idolList.length === 0) return;

    idolList.forEach((item, index) => {
      if (item.idol_id === id) {
        if (idolList[index].idol_avatar_url !== avatarUrl) {
          dispatch(setAvatarUrl(idolList[index].idol_avatar_url));
        }
        if (idolList[index].idol_cover_url !== coverUrl) {
          dispatch(setCoverUrl(idolList[index].idol_cover_url));
        }
        if (idolList[index].idol_full_name !== fullName) {
          dispatch(setFullName(idolList[index].idol_full_name));
        }
        if (idolList[index].idol_nick_name !== nickName) {
          dispatch(setNickName(idolList[index].idol_nick_name));
        }
        if (idolList[index].idol_is_official !== isOfficial) {
          dispatch(setIsOfficial(idolList[index].idol_is_official));
        }
        if (idolList[index].idol_uuid !== idolUuid) {
          dispatch(setIdolUuid(idolList[index].idol_uuid));
        }
      }
    });
  };

  // 只顯示目前關鍵字的結果（關鍵字為空時不會有結果，getSearchList 不送出空關鍵字）
  const visibleSearchResult = searchResult?.keyword === searchKeyword ? searchResult.list : null;
  // 還沒有目前關鍵字的結果（打字、組字中尚未送出，或請求中）時顯示「載入中」；搜尋失敗的關鍵字不顯示
  const isSearchPending = searchKeyword !== "" && !visibleSearchResult && searchFailedKeyword !== searchKeyword;

  // 輸入 search 顯示搜尋結果
  const handleSearchInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchKeyword(inputValue);
    setSearchResultVisible(inputValue !== "");
  };

  // 點擊 search 顯示搜尋結果
  const handleSearchInputFocus = () => {
    setSearchResultVisible(true);
  };

  // 點擊搜尋以外區域會隱藏搜尋結果
  const handleDocumentClick = (e) => {
    if (searchInputRef.current && !searchInputRef.current.contains(e.target)) {
      setSearchResultVisible(false);
    }
  };

  const [pageParams, setPageParams] = useState({
    keyword: searchKeyword,
    page: 1,
    limit: 100,
  });

  const getSearchList = async (params) => {
    const seq = ++searchSeqRef.current;
    // 清空關鍵字時也要讓途中的請求作廢（序號已在上面前進）
    if (!params.keyword) return;
    setSearchFailedKeyword(null);
    try {
      const res = await Axios.post("/search", params);
      if (seq !== searchSeqRef.current) return;
      switch (res.data.status_code) {
        case "SYSTEM_1000":
          setSearchResult({ keyword: params.keyword, list: res.data.data });
          break;
        default:
          setSearchFailedKeyword(params.keyword);
          setCurrentStatusCode(res.data.status_code);
          statusModalRef.current?.show();
          break;
      }
    } catch (error) {
      if (seq !== searchSeqRef.current) return;
      setSearchFailedKeyword(params.keyword);
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Get search list error:', error);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    // 組字中按 Enter 是選字，不是送出
    if (e.key === "Enter" && !isImeComposingKey(e)) {
      e.preventDefault();
      if (searchKeyword.trim() === "") return;
      // 前往搜尋結果頁，與下拉選單的「進階搜尋」連結相同。整頁跳轉：SearchPage 只在掛載時搜尋，
      // 已在 /search 時用站內導頁不會重新搜尋
      markPageUnloading();
      window.location.href = `/search?q=${encodeURIComponent(searchKeyword)}`;
    }
  };

  useEffect(() => {
    setPageParams((prevParams) => ({ ...prevParams, keyword: debouncedSearchKeyword }));
  }, [debouncedSearchKeyword]);

  useEffect(() => {
    getSearchList(pageParams);
  }, [pageParams]);

  //如果沒有 token 則導回首頁登入（除了靜態頁面）
  useEffect(() => {
    const staticPages = [
      "/terms",
      "/privacy",
      "/chat-token",
      "/contact",
      "/cookies",
      "/rules",
      "/api-docs",
      "/sign-up",
      "/forget-password",
      "/",
    ];
    // replace：用 push 的話，按上一頁會回到需要登入的頁面、又被導回首頁，形成迴圈
    if (!token && !staticPages.includes(location.pathname)) {
      navigate("/", { replace: true });
      return;
    }

    // 只在登入狀態下同步當前偶像資料
    if (token && idolList && idolList.length > 0) {
      syncIdolData();
    }

    // 監聽點擊區塊以外事件（搜尋功能相關）
    if (token) {
      document.addEventListener("click", handleDocumentClick);
    }

    // 组件卸载
    return () => {
      if (token) {
        document.removeEventListener("click", handleDocumentClick);
      }
    };
  }, [token, location.pathname, idolList]);

  const mainIdol = (
    <Dropdown.Item href={`/idol/${idolUuid || id}`} className="p-0">
      <IdolCoverCard
        coverUrl={coverUrl}
        avatarUrl={avatarUrl}
        nickName={nickName}
        isOfficial={isOfficial}
        className="mb-3"
      />
    </Dropdown.Item>
  );

  const IdolItemNode = ({ idol, onItemClick }) => {
    return (
      <Dropdown.Item
        as="button"
        key={idol.idol_id}
        className="d-flex align-items-center border-top py-2"
        onClick={() => onItemClick(idol)}
      >
        <IdolInline idol={idol} />
      </Dropdown.Item>
    );
  };

  const idolSwitch = async (selectedIdol) => {
    // 切換進行中忽略再次點擊，避免並行請求互相覆蓋
    if (idolSwitchingRef.current) return;
    idolSwitchingRef.current = true;
    try {
      const res = await Axios.post(
        "/idol/switch-check",
        {
          idol_id: selectedIdol.idol_id,
        }
      );
      if (res.data.status_code === "SYSTEM_1000") {
        dispatch(setIdolId(selectedIdol.idol_id));
        dispatch(setIdolUuid(selectedIdol.idol_uuid));
        dispatch(setNickName(selectedIdol.idol_nick_name));
        dispatch(setFullName(selectedIdol.idol_full_name));
        dispatch(setAvatarUrl(selectedIdol.idol_avatar_url));
        dispatch(setCoverUrl(selectedIdol.idol_cover_url));
        dispatch(setIsOfficial(selectedIdol.idol_is_official));
        // redux-persist 是非同步寫入 localStorage，整頁跳轉會中斷未完成的寫入，
        // 必須先 flush 確保新偶像狀態落地，否則重載後會 rehydrate 回舊偶像
        await persistor.flush();
        // 導向該偶像的頁面（即將整頁跳轉，不解鎖）
        markPageUnloading();
        window.location.href = `/idol/${selectedIdol.idol_uuid}`;
        return;
      }
      setCurrentStatusCode(res.data.status_code);
      statusModalRef.current?.show();
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Failed to switch idol:', error);
      }
    }
    idolSwitchingRef.current = false;
  };

  const idolItem = (idolList || [])
    .filter((item) => {
      if (!idolList || idolList.length < 2) {
        return null;
      } else {
        return item.idol_id !== id;
      }
    })
    .map((item) => {
      // 停權偶像不可切換，改用 disabled 的灰階列
      if (item.idol_is_suspended) {
        return <SuspendedIdolItem key={item.idol_id} idol={item} />;
      }
      return (
        <IdolItemNode key={item.idol_id} idol={item} onItemClick={idolSwitch} />
      );
    });

  const signout = useSignout();
  const handleSignout = async () => {
    try {
      const statusCode = await signout();
      if (statusCode !== "SYSTEM_1000") {
        setCurrentStatusCode(statusCode);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Signout error:', error);
      }
    }
  };

  const currentLanguage = i18n.language;
  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("language", language);
  };

  const chatRooms = useSelector((state) => state.chat.rooms);

  const checkNotify = () => {
    const totalUnread = chatRooms?.reduce((sum, room) => sum + (room.is_read_count || 0), 0) || 0;
    setUnreadCount(totalUnread);
  };

  useEffect(() => {
    if (token) {
      checkNotify();
    }
  }, [chatRooms, token]);

  return (
    <>
    <Navbar
      bg="secondary"
      expand="lg"
      className="navbar-wrap position-fixed w-100 shadow-sm"
    >
      <Container fluid="lg"
        className={`flex-wrap flex-sm-nowrap gap-2 ${!effectiveToken ? "justify-content-center" : ""
          }`}
      >
        <Navbar.Brand
          className={`navbar-logo ${!effectiveToken ? "mx-auto" : ""}`}
          href="/"
        >
          <img
            src={logo_small}
            className="img-fluid logo_small mt-2"
            alt="logo"
          />
        </Navbar.Brand>
        {/* Search - 僅登入且選擇偶像後顯示 */}
        {effectiveToken && id && (
          <Form
            ref={searchInputRef}
            className="position-relative order-3 order-sm-2 flex-grow-1 flex-sm-grow-0 me-auto"
          >
            <FontAwesomeIcon
              icon="fa-solid fa-magnifying-glass"
              className="position-absolute search-icon"
            />
            <Form.Control
              type="search"
              placeholder={t("search")}
              className="search-input ps-5 py-2 rounded-5 border-0"
              aria-label="Search"
              onFocus={handleSearchInputFocus}
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchSubmit}
              {...searchCompositionProps}
            />
            <div
              className={`search-result position-absolute shadow rounded-3 bg-white p-3 d-flex gap-3 flex-column ${searchResultVisible ? "" : "d-none"
                }`}
            >
              {visibleSearchResult &&
                visibleSearchResult.map((item) =>
                  item.method === "post" ? (
                    <a
                      key={item.id}
                      className="link-none pointer d-flex align-items-center gap-2"
                      href={`/post/${item.uuid}`}
                    >
                      {item.image ? (
                        <div className="search-img flex-shrink-0">
                          <SafeImg
                            src={item.image}
                            className="rounded-pill"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            alt="idol-img"
                          />
                        </div>
                      ) : (
                        <div className="bg-light search-img rounded-pill d-flex align-items-center justify-content-center">
                          <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                        </div>
                      )}
                      <h6 className="mb-0">{item.title}</h6>
                    </a>
                  ) : item.method === "idol" ? (
                    <a
                      key={item.id}
                      className="link-none pointer d-flex align-items-center gap-2"
                      href={`/idol/${item.uuid}`}
                    >
                      {item.image ? (
                        <div className="search-img flex-shrink-0">
                          <SafeImg
                            src={item.image}
                            className="rounded-pill"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            alt="idol-img"
                          />
                        </div>
                      ) : (
                        <div className="search-img flex-shrink-0">
                          <SafeImg
                            className="img-fluid rounded-pill border border-white"
                            src={item.idol_avatar_url || placeholder_portrait_35x35}
                          />
                        </div>
                      )}
                      <h6 className="mb-0">{item.title}</h6>
                    </a>
                  ) : item.method === "world" ? (
                    <a
                      key={item.id}
                      className="link-none pointer d-flex align-items-center gap-2"
                      href={`/world-view/${item.uuid}`}
                    >
                      {item.image ? (
                        <div className="search-img flex-shrink-0">
                          <SafeImg
                            src={item.image}
                            className="rounded-pill"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
                            alt="idol-img"
                          />
                        </div>
                      ) : (
                        <div className="bg-light search-img rounded-pill d-flex align-items-center justify-content-center">
                          <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                        </div>
                      )}
                      <h6 className="mb-0">{item.title}</h6>
                    </a>
                  ) : null
                )}

              {visibleSearchResult &&
                visibleSearchResult.length > 0 && (
                  <a
                    className="link-none pointer d-flex align-items-center gap-2"
                    href={`/search?q=${encodeURIComponent(searchKeyword)}`}
                  >
                    <div className="bg-light search-img rounded-pill d-flex align-items-center justify-content-center">
                      <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" />
                    </div>
                    <h6 className="mb-0">{t("advanced_search")}</h6>
                  </a>
                )}

              {isSearchPending && (
                <span className="text-gray">{t("loading")}</span>
              )}

              {/* 沒有搜尋結果 */}
              {visibleSearchResult &&
                visibleSearchResult.length === 0 && (
                  <span className="text-gray">{t("no_search_result")}</span>
                )}

              {/* 尚未輸入關鍵字 */}
              {searchKeyword === "" && (
                <span className="text-gray">{t("no_search_result")}</span>
              )}
            </div>
          </Form>
        )}

        <div className="d-flex gap-3 order-2 order-sm-3 nav-utilities">
          {/* language */}
          <Dropdown className="nav-language">
            <Dropdown.Toggle as={IconToggle} id="navbarLanguageDropdown" tooltip={t('language_switch')} />
            <Dropdown.Menu>
              <Dropdown.Item
                as="button"
                onClick={() => handleLanguageChange("en")}
                className={currentLanguage === "en" ? "fw-bold" : ""}
              >
                {t("english")}
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                onClick={() => handleLanguageChange("zh")}
                className={currentLanguage === "zh" ? "fw-bold" : ""}
              >
                {t("traditional_chinese")}
              </Dropdown.Item>
              <Dropdown.Item
                as="button"
                onClick={() => handleLanguageChange("ja")}
                className={currentLanguage === "ja" ? "fw-bold" : ""}
              >
                {t("japanese")}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          {/* 其他功能 - 僅登入後顯示 */}
          {effectiveToken && id && (
            <>
              {/* chats */}
              <Nav.Link href="/chats">
                <IconButton
                  size="lg"
                  className="bg-white position-relative"
                  tooltip={t('message')}
                  placement="bottom"
                  badge={unreadCount > 0 && <span className="nav-utilities-status">{unreadCount > 99 ? "99+" : unreadCount}</span>}
                >
                  <FontAwesomeIcon icon="fa-solid fa-message" />
                </IconButton>
              </Nav.Link>
              {/* notification */}
              <NotificationDropdown />
              {/* profile */}
              <Dropdown className="nav-profile">
                <Dropdown.Toggle
                  as={AvatarToggle}
                  id="navbarScrollingDropdown"
                  src={avatarUrl}
                  placeholder={placeholder_portrait_40x40}
                />
                <Dropdown.Menu align="end">
                  {mainIdol}
                  <div className="rounded border shadowA nav-idol-list my-3 p-3">
                    {!idolList || idolList.length < 2 ? (
                      ""
                    ) : (
                      <>
                        <p className="mb-2">{t("switch_idol")}</p>
                        <div className="idol-list-scrollable">{idolItem}</div>
                      </>
                    )}
                    <Button
                      href="/idol-create"
                      className="btn btn-primaryF w-100 mt-2"
                    >
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <FontAwesomeIcon icon={["fas", "plus"]} />
                        <span>{t("create_idol")}</span>
                      </div>
                    </Button>
                    <Button
                      href="/idol-list"
                      className="btn btn-outline-primaryF w-100 mt-2"
                    >
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <FontAwesomeIcon icon={["fas", "universal-access"]} />
                        <span>{t("view_all_idol")}</span>
                      </div>
                    </Button>
                  </div>

                  <Dropdown.Item href="/wallet">
                    <div className="btn btn-primaryF w-100">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <FontAwesomeIcon icon={["fas", "wallet"]} />
                        <span>{t("my_wallet")}</span>
                      </div>
                    </div>
                  </Dropdown.Item>

                  <Dropdown.Item href="/settings">
                    <div className="btn btn-primary w-100">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <FontAwesomeIcon icon={["fas", "gear"]} />
                        <span>{t("setting")}</span>
                      </div>
                    </div>
                  </Dropdown.Item>

                  <Dropdown.Item as="button" onClick={handleSignout}>
                    <div className="btn btn-neutral w-100">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <FontAwesomeIcon icon={["fas", "right-from-bracket"]} />
                        <span>{t("signout")}</span>
                      </div>
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          )}
        </div>
      </Container>
    </Navbar>
    <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </>
  );
}

export default MainNav;
